import { Capacitor } from "@capacitor/core";
import http from "@shared/api/http";

/**
 * Web push for training reminders. The browser hands out a subscription
 * (endpoint + keys) once the patient allows notifications; the backend keeps
 * one row per device and sends the reminder at the chosen hour
 * (sophy_web: PushSubscription). Everything here is guarded, because the
 * APIs are missing in older browsers, in iOS Safari outside an installed
 * web app, and inside the Android WebView.
 */

const SW_URL = `${process.env.PUBLIC_URL || ""}/sw.js`;
const API = "/:locale/v2/:token/push_subscription.json";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission() {
  return typeof Notification !== "undefined" ? Notification.permission : "unsupported";
}

/**
 * Registers the worker once; safe to call on every load. Not in the store app:
 * its WebView has no Push API, and a worker there would outlive bundle switches.
 */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || Capacitor.isNativePlatform()) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL);
  } catch (error) {
    console.error("Service worker registration failed:", error);
    return null;
  }
}

/** The VAPID key and this patient's devices, from the backend. */
export async function fetchServerState() {
  const response = await http.get(API);
  return response.data;
}

/** The browser's own subscription for this origin, if any. */
export async function getBrowserSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Vienna";
  } catch (e) {
    return "Europe/Vienna";
  }
}

const toRecord = (subscription, settings) => {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent,
    timezone: deviceTimezone(),
    reminder_hour: settings.reminderHour,
    reminders_enabled: settings.enabled !== false,
  };
};

/**
 * Asks for permission (must run from a click), subscribes the browser and
 * registers the device with the backend. Rejects with { code: "denied" }
 * when the patient declines.
 */
export async function subscribe(vapidPublicKey, settings = { reminderHour: 18 }) {
  if (!isPushSupported()) throw Object.assign(new Error("unsupported"), { code: "unsupported" });

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw Object.assign(new Error("denied"), { code: "denied" });

  const registration = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  const ready = await navigator.serviceWorker.ready;
  const manager = (registration || ready).pushManager;

  const existing = await manager.getSubscription();
  const subscription =
    existing ||
    (await manager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }));

  const response = await http.post(API, { push_subscription: toRecord(subscription, settings) });
  return response.data;
}

/** Reminder hour / on-off for this device. */
export async function updateSettings(endpoint, settings) {
  const response = await http.put(API, {
    endpoint,
    push_subscription: {
      timezone: deviceTimezone(),
      reminder_hour: settings.reminderHour,
      reminders_enabled: settings.enabled !== false,
    },
  });
  return response.data;
}

/** Removes the device from the backend and drops the browser subscription. */
export async function unsubscribe() {
  const subscription = await getBrowserSubscription();
  if (!subscription) return false;
  await http.delete(API, { data: { endpoint: subscription.endpoint } });
  await subscription.unsubscribe();
  return true;
}
